<template>
  <header class="flex flex-col relative bg-[#FFF]">
    <div class="bg-blue flex justify-center relative z-40">
      <div
        class="text-white text-sm py-2 lg:py-3 px-4 flex max-md:w-full justify-center items-center gap-x-8 font-instrumentSans">
        <p class="hidden md:flex text-cyan-50">
          {{ t('main.notice') }}
          <i class="bi bi-dot text-xl -mt-1 -mb-1.5"></i>
          {{ t('main.notice_2') }}
        </p>
      </div>
    </div>

    <nav class="bg-white border-b border-light flex justify-center items-center z-30">
      <div
        class="relative top-0 z-30 max-w-[1200px] flex justify-between items-center px-4 lg:px-8 py-3 w-full">
        <RouterLink :to="{ name: 'home.index' }">
          <img src="/images/logo.svg" alt="Bistroo" class="h-[28px] lg:h-[30px] lg:w-[200px]"/>
        </RouterLink>

        <ClientOnly>
          <Menu :show-dropdown="showMenu"/>
        </ClientOnly>

        <div class="flex items-center gap-x-4 whitespace-nowrap">
          <a href="https://merchant.bistroo.nl/login" target="_blank" class="hidden lg:flex font-bold">{{
              t('main.login_text')
            }}
          </a>

          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="hidden lg:flex btn btn-primary text-sm uppercase !font-bold">
            {{ t('main.start_free_text') }}
          </a>

          <a href="#" class="flex lg:hidden max-md:order-1" @click.prevent="toggleShowMenu">
            <i class="bi bi-list text-2xl"></i>
          </a>

          <div class="h-8 w-8 flex items-center justify-center relative group max-md:order-0">
            <button class="focus:outline-none !rounded-full overflow-hidden h-[24px] w-[24px]">
              <img :src="currentFlagSrc" alt="Flag" class="cursor-pointer h-full object-cover"/>
            </button>

            <div class="w-max absolute top-full right-0 pt-5 lg:-right-1 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 lg:pointer-events-none lg:group-hover:pointer-events-auto transition-all ease-in-out">
              <div
                class="p-4 bg-white flex flex-col gap-y-2 drop-shadow rounded-xl">
                <a
                  href="#"
                  class="flex items-center gap-x-4 text-lighter font-semibold hover:text-dark"
                  @click.prevent="setLocale('nl')">
                  <img class="h-8 w-8 rounded-lg" :src="asset('/images/flags/nl.svg')" alt="Dutch"/>
                  <span>Nederlands</span>
                </a>

                <a
                  href="#"
                  class="flex items-center gap-x-4 text-lighter font-semibold hover:text-dark"
                  @click.prevent="setLocale('en')">
                  <img
                    class="h-8 w-8 rounded-lg"
                    :src="asset('/images/flags/en.svg')"
                    alt="English"/>
                  <span>English</span>
                </a>

                <a
                  href="#"
                  class="flex items-center gap-x-4 text-lighter font-semibold hover:text-dark"
                  @click.prevent="setLocale('de')">
                  <img
                    class="h-8 w-8 rounded-lg"
                    :src="asset('/images/flags/de.svg')"
                    alt="German"/>
                  <span>Deutsch</span>
                </a>

                <a
                  href="#"
                  class="flex items-center gap-x-4 text-lighter font-semibold hover:text-dark"
                  @click.prevent="setLocale('fr')">
                  <img
                    class="h-8 w-8 rounded-lg"
                    :src="asset('/images/flags/fr.svg')"
                    alt="French"/>
                  <span>Français</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Menu from '@/components/Menu.vue'
import ClientOnly from '@/components/ssr/ClientOnly.vue'
import { useI18n } from 'vue-i18n'
import { type SupportedLocale, supportedLocales } from '@/config/supportedLocales.ts'
import { asset } from '@/utils/asset.ts'
import { vTrackEvent } from '@/directives/vTrackEvent.ts'
import { publicPath } from '@/utils/publicPath.ts'

const { t } = useI18n()
const i18n = useI18n()

const showMenu = ref<boolean>(false)

const locale = computed<SupportedLocale>(() => i18n.locale.value as SupportedLocale)

const currentFlagSrc = computed<string>(() => publicPath(`/images/flags/${locale.value}.svg`))

const setLocale = (locale: SupportedLocale) => {
  if (!supportedLocales.includes(locale)) {
    throw new Error(`"${locale}" is not supported.`)
  }

  i18n.locale.value = locale
  localStorage.setItem('preferredLocale', locale)
}

const toggleShowMenu = () => {
  showMenu.value = !showMenu.value
}
</script>

<style scoped></style>
